import { Col, Form, Row } from "antd";
import { useRouter } from "next/router";
import React, { FC, useEffect, useState } from "react";
import { LatLng } from "use-places-autocomplete";
import getChangedFields from "../../helpers/getChangedFields";
import { parseCoords, stringifyCoordinates } from "../../helpers/jsonHandler";
import {
  useCreateDepotMutation,
  useEditDepotMutation
} from "../../lib/api/endpoints/Depots/depotEndpoints";
import { StopTypes } from "../../lib/types/components/AddEditProps";
import { GetDepotProfileResponse } from "../../lib/types/depots";
import { requiredField } from "../../lib/validation/InputValidations";
import { handleAPIRequests } from "../../utils/handleAPIRequests";
import Button from "../Shared/Button";
import Input from "../Shared/Input";
import OSMInput from "../Shared/Input/OSMInput";
import ModalWrapper from "./ModalWrapper";

type Types = {
  isVisible: boolean;
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>;
  isEditing?: boolean;
  currentDepotData?: GetDepotProfileResponse;
};

type FormTypes = {
  name: string;
  coordinates: string;
  location: string;
  kgkm: number;
  pricingPreference:
    | "DEFAULT_PRICING"
    | "DEPOT_PRICING"
    | "TRUCK_PRICING"
    | "CATEGORY_PRICING";
  erpNextWarehouse?: string;
};

type DepotComparisonData = {
  name: string;
  location: string;
  coordinates: string;
  kgkm: number;
  pricingPreference: string;
  erpNextWarehouse?: string;
};

const NewDepotModal: FC<Types> = ({
  isVisible,
  setIsVisible,
  isEditing,
  currentDepotData
}) => {
  const [location, setLocation] = useState<StopTypes | undefined>();

  const router = useRouter();
  const [form] = Form.useForm();

  const { id: depotId } = router.query;

  const handleCancel = () => {
    setIsVisible(false);
    form.resetFields();
    setLocation(undefined);
  };

  const [createNewDepot, { isLoading }] = useCreateDepotMutation();
  const [editDepot, { isLoading: isEditLoading }] = useEditDepotMutation();

  const handleCreateDepotSuccess = () => {
    handleCancel();
  };

  useEffect(() => {
    if (isEditing && currentDepotData) {
      const currentCoordinates = currentDepotData?.payload?.depot?.coordinates;
      const currentLocationName = currentDepotData?.payload?.depot?.location;
      const currentName = currentDepotData?.payload?.depot?.name;

      let parsedCoordinates;

      try {
        parsedCoordinates = currentCoordinates
          ? parseCoords(currentCoordinates)
          : null;
      } catch (error) {
        parsedCoordinates = null;
      }

      setLocation({
        name: currentLocationName,
        coordinates: parsedCoordinates as LatLng
      });
      form.setFieldsValue({
        name: currentName,
        location: currentLocationName,
        pricingPreference: currentDepotData?.payload?.depot?.pricingPreference,
        kgkm: currentDepotData?.payload?.depot?.kgkm
      });
    }
  }, [currentDepotData, form, isEditing, isVisible]);

  const onFinish = (values: FormTypes) => {
    if (isEditing && currentDepotData) {
      // Create original data object from current depot data
      const originalData: DepotComparisonData = {
        name: currentDepotData?.payload?.depot?.name || "",
        location: currentDepotData?.payload?.depot?.location || "",
        coordinates: currentDepotData?.payload?.depot?.coordinates || "",
        kgkm: currentDepotData?.payload?.depot?.kgkm || 0,
        pricingPreference:
          currentDepotData?.payload?.depot?.pricingPreference || ""
      };

      // Create updated data object from form values
      const updatedData: DepotComparisonData = {
        name: values.name,
        location: location?.name || "",
        coordinates: stringifyCoordinates(location?.coordinates) || "",
        kgkm: values.kgkm,
        pricingPreference: values.pricingPreference,
        erpNextWarehouse: values.erpNextWarehouse
      };

      // Get only changed fields
      const { changedFields, isEmpty } = getChangedFields(
        originalData,
        updatedData
      );

      // If no fields have changed, just close the modal
      if (isEmpty) {
        handleCreateDepotSuccess();
        return;
      }

      // Send only the changed fields
      handleAPIRequests({
        request: editDepot,
        ...changedFields,
        id: depotId,
        showSuccess: true,
        handleSuccess: handleCreateDepotSuccess
      });
    } else {
      // For creating new depot, send all values
      handleAPIRequests({
        request: createNewDepot,
        ...values,
        location: location?.name,
        coordinates: stringifyCoordinates(location?.coordinates),
        showSuccess: true,
        handleSuccess: handleCreateDepotSuccess
      });
    }
  };

  return (
    <ModalWrapper
      title={isEditing ? "Edit depot" : "New depot"}
      isModalVisible={isVisible}
      setIsModalVisible={setIsVisible}
      loading={isLoading || isEditLoading}
      onCancel={handleCancel}
      destroyOnClose
      width={600}
      footerContent={
        <Button
          form="createNewDepot"
          loading={isLoading || isEditLoading}
          type="primary"
          htmlType="submit"
          disabled={false}
        >
          Save
        </Button>
      }
    >
      <Form
        name="createNewDepot"
        onFinish={onFinish}
        layout="vertical"
        form={form}
        title="New depot"
        id="createNewDepot"
      >
        <Row justify="space-between" gutter={[24, 24]}>
          <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
            <Input
              name="name"
              type="text"
              label="Name"
              placeholder="Enter depot name"
              rules={requiredField("Depot name")}
            />
          </Col>

          <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
            <OSMInput
              label="Location"
              name="location"
              setSelectedLocation={setLocation}
            />
          </Col>

          <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
            <Input
              name="kgkm"
              type="text"
              inputType="number"
              step=".01"
              label="KGKM"
              suffixIcon="Rwf"
              placeholder="Enter KGKM price"
            />
          </Col>

          <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
            <Input
              name="pricingPreference"
              type="select"
              label="Pricing preference"
              placeholder="Pricing preference"
              options={[
                { label: "Default", value: "DEFAULT_PRICING" },
                { label: "Depot", value: "DEPOT_PRICING" },
                { label: "Truck", value: "TRUCK_PRICING" },
                { label: "Category", value: "CATEGORY_PRICING" }
              ]}
            />
          </Col>
        </Row>
      </Form>
    </ModalWrapper>
  );
};

export default NewDepotModal;
